import { signOut, getRedirectResult, type User } from 'firebase/auth'
import { logEvent } from 'firebase/analytics'
import { auth, analytics } from '../firebase'
import { debug } from '../utils/debug'

/**
 * Get and clear the stored redirect destination
 */
export function getAndClearRedirectDestination(): string | null {
  const destination = localStorage.getItem('redirectAfterLogin')
  if (destination) {
    localStorage.removeItem('redirectAfterLogin')
    return destination
  }
  return null
}

/**
 * Handle redirect result from OAuth providers
 * Returns both the user and the intended redirect destination
 */
export async function handleRedirectResult(): Promise<{
  user: User | null
  redirectTo: string | null
}> {
  try {
    debug.auth('Checking for redirect result...')
    const result = await getRedirectResult(auth)

    if (result && result.user) {
      debug.auth('Redirect result: User signed in', result.user.email)

      // Log successful login event
      if (analytics) {
        logEvent(analytics, 'login', {
          method: 'google',
        })
      }

      // Get the intended redirect destination
      const redirectTo = getAndClearRedirectDestination()
      debug.auth('Redirect destination after login:', redirectTo)

      return { user: result.user, redirectTo }
    } else {
      debug.auth('Redirect result: No user found')
      return { user: null, redirectTo: null }
    }
  } catch (error) {
    debug.error('Error handling redirect result:', error)

    // Log login error
    if (analytics) {
      logEvent(analytics, 'login_error', {
        method: 'google',
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }

    // Return error state instead of throwing
    return { user: null, redirectTo: null }
  }
}

/**
 * Sign out the current user
 */
export async function logout(): Promise<void> {
  try {
    await signOut(auth)

    // Log logout event
    if (analytics) {
      logEvent(analytics, 'logout')
    }
  } catch (error) {
    // TODO: Replace with proper error handling before production deployment
    debug.error('Logout error:', error)
    throw error
  }
}

/**
 * Check if user is currently authenticated
 */
export function isAuthenticated(): boolean {
  return auth.currentUser !== null
}

/**
 * Get the current authenticated user
 */
export function getCurrentUser(): User | null {
  return auth.currentUser
}
